import type { LogsEvent } from '@datadog/browser-logs';
import type { RumEvent } from '@datadog/browser-rum';

// Network failures that are noise, not defects. Both SDKs forward every failed
// request by default — RUM as an error event, Logs via forwardErrorsToLogs — so
// both inits need the same filter, or the noise just moves from one explorer to
// the other (it lived in Logs for four months after the RUM side alone was
// filtered).
//
// - The updater polls /assets/VERSION every 5 minutes for as long as a tab
//   stays open. A laptop waking up or Wi-Fi roaming fails the poll with a
//   status-0 fetch error the poll itself already tolerates, and a single tab
//   left open overnight produces a dozen of them.
// - Plausible is fire-and-forget analytics loaded from index.html. Its event
//   POSTs fail in the same offline windows, and some browsers block the host
//   outright; either way nothing in the app depends on the request.
// - Auth0's silent token renewal (POST /oauth/token) fails the same way when
//   the browser will not send the request — a laptop between networks, or
//   whatever a given store's machine does on a schedule. Two things make it the
//   loudest source in the explorer rather than the largest: auth0-spa-js retries
//   three times with NO backoff (DEFAULT_SILENT_TOKEN_RETRY_COUNT), and the
//   caller then climbs its own ladder, so roughly 15-20 real incidents a month
//   arrive as ~170 lines. Every one observed so far self-heals — the store
//   WebSocket keeps delivering and no API call fails. A genuine Auth0 outage
//   answers 4xx/5xx, which never reaches this predicate: those go to RUM via
//   throwIfNotOk's addError, not through network forwarding.
const VERSION_POLL_PATH = '/assets/VERSION';
const PLAUSIBLE_ORIGIN = 'https://plausible.io/';
const TOKEN_RENEWAL_PATH = '/oauth/token';

const isIgnoredNetworkUrl = (url: string | undefined): boolean =>
  url !== undefined &&
  (url.endsWith(VERSION_POLL_PATH) ||
    url.startsWith(PLAUSIBLE_ORIGIN) ||
    url.endsWith(TOKEN_RENEWAL_PATH));

/** RUM `beforeSend`: keep everything except a failed request to an ignored URL. */
export const keepRumEvent = (event: RumEvent): boolean =>
  !(
    event.type === 'error' &&
    event.error.source === 'network' &&
    isIgnoredNetworkUrl(event.error.resource?.url)
  );

/** Logs `beforeSend`: keep everything except a failed request to an ignored URL. */
export const keepLogsEvent = (event: LogsEvent): boolean =>
  !(event.origin === 'network' && isIgnoredNetworkUrl(event.http?.url));
